import * as React from "react";
import { Loader2, Sparkles, Upload, Wand2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useThemeStudio } from "@/features/theming/theme-studio-context";
import { seedFromLogoFile } from "@/features/theming/logo-seed";

/** A spread of pleasant seeds for "Surprise me" (varied hues; the engine does the
 *  rest). No randomness needed — we rotate through them. */
const SURPRISE_SEEDS = ["#7c3aed", "#e0379a", "#11d4a4", "#f5b945", "#4f6ef7", "#ff5d5d"];

/**
 * "Give us anything" — the single ingest bar that seeds the generated candidates.
 * A URL (real backend ingest), a logo (dominant colour), or nothing (a surprise
 * seed). Replaces the old raw-hex-only URL box as the primary entry point.
 */
export function ThemeGenerator() {
  const { generateFrom, generating, ingestNote } = useThemeStudio();
  const [url, setUrl] = React.useState("");
  const fileRef = React.useRef<HTMLInputElement>(null);
  const surpriseIdx = React.useRef(0);

  const canUrl = url.trim().length > 0 && !generating;

  async function onLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    const seed = await seedFromLogoFile(file);
    await generateFrom({ kind: "logo", seed: seed ?? undefined });
  }

  function onSurprise() {
    const seed = SURPRISE_SEEDS[surpriseIdx.current % SURPRISE_SEEDS.length]!;
    surpriseIdx.current += 1;
    void generateFrom({ kind: "none", seed });
  }

  return (
    <div className="flex flex-col gap-2.5 rounded-[12px] border border-border bg-panel-2/40 p-4">
      <Label className="flex items-center gap-1.5 text-[13px] font-semibold">
        <Sparkles className="size-4 text-accent" /> Start from anything
      </Label>
      <p className="text-[13px] text-muted-foreground">
        A website, your logo, or nothing at all — we’ll generate full themes on the real app.
        Nothing saves until you Publish.
      </p>

      <div className="flex flex-wrap gap-2">
        <div className="flex min-w-[240px] flex-1 gap-2">
          <Input
            type="url"
            inputMode="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canUrl) {
                e.preventDefault();
                void generateFrom({ kind: "url", url: url.trim() });
              }
            }}
            placeholder="https://yourbrand.com"
            disabled={generating}
            className="text-[13px]"
          />
          <Button
            type="button"
            onClick={() => void generateFrom({ kind: "url", url: url.trim() })}
            disabled={!canUrl}
            className="shrink-0"
          >
            {generating ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
            {generating ? "Generating…" : "Generate"}
          </Button>
        </div>

        <Button type="button" variant="subtle" onClick={() => fileRef.current?.click()} disabled={generating}>
          <Upload className="size-4" /> Use my logo
        </Button>
        <Button type="button" variant="ghost" onClick={onSurprise} disabled={generating}>
          <Wand2 className="size-4" /> Surprise me
        </Button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onLogo} />
      </div>

      {ingestNote && (
        <p
          role="status"
          className="rounded-[10px] border border-[rgba(245,185,69,.28)] bg-[rgba(245,185,69,.08)] px-3 py-2.5 text-[13px] leading-[1.45] text-[#f5b945]"
        >
          {ingestNote} — generated from a default seed instead.
        </p>
      )}
    </div>
  );
}
